import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ResponsiveContainer, Area, AreaChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { FiBarChart2, FiSearch, FiTrendingDown, FiTrendingUp } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import { DisclosureCard, HelpIcon, useLanguage } from '../components';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import './MarketData.css';

const periods = ['1W', '1M', '3M', '6M', '1Y', '5Y', 'MAX'];

const hasMetricValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed !== '' && trimmed.toUpperCase() !== 'N/A';
  }
  if (typeof value === 'number') return Number.isFinite(value);
  return true;
};

const MarketData = ({ apiBase }) => {
  const { lang, t, tt } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-US';

  const interpolate = useCallback((template, values = {}) => {
    let result = String(template ?? '');
    Object.entries(values).forEach(([key, value]) => {
      result = result.split(`{{${key}}}`).join(String(value));
    });
    return result;
  }, []);
  const [stocks, setStocks] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [stockDetails, setStockDetails] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('1Y');
  const [error, setError] = useState(null);
  const [selectedSector, setSelectedSector] = useState('');
  const [selectedExchange, setSelectedExchange] = useState('');
  const [sectors, setSectors] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [latestPriceDate, setLatestPriceDate] = useState(null);
  const [latestPriceTickerCount, setLatestPriceTickerCount] = useState(0);
  const [tickersWithPrice, setTickersWithPrice] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = React.useRef(null);

  const handleSelectStock = useCallback((ticker, options = {}) => {
    const normalizedTicker = String(ticker || '').trim().toUpperCase();
    if (!normalizedTicker) return;

    const tickerChanged = normalizedTicker !== selectedStock;

    if (tickerChanged) {
      setStockDetails(null);
      setPriceHistory([]);
      setError(null);
    }

    setSelectedStock(normalizedTicker);

    if (options.syncUrl !== false) {
      const nextParams = new URLSearchParams(searchParams);
      if (nextParams.get('ticker') !== normalizedTicker) {
        nextParams.set('ticker', normalizedTicker);
        setSearchParams(nextParams, { replace: true });
      }
    }

    if (options.closeSuggestions) {
      setShowSuggestions(false);
    }
  }, [searchParams, selectedStock, setSearchParams]);

  const fetchAllStocks = useCallback(async () => {
    try {
      const response = await axios.get(`${apiBase}/stocks/all`);
      setStocks(response.data.stocks || []);
      setLatestPriceDate(response.data.latest_price_date || null);
      setLatestPriceTickerCount(response.data.latest_price_ticker_count || 0);
      setTickersWithPrice(response.data.tickers_with_price || 0);
    } catch (fetchError) {
      console.error('Error fetching stocks:', fetchError);
      setError(tt('Unable to load market data. Please try again.'));
    }
  }, [apiBase, tt]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await axios.get(`${apiBase}/stocks/filters/options`);
      setSectors(response.data.sectors || []);
      setExchanges(response.data.exchanges || []);
    } catch (fetchError) {
      console.error('Error fetching filter options:', fetchError);
    }
  }, [apiBase]);

  const fetchStockDetails = useCallback(async (ticker) => {
    setDetailsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiBase}/stocks/${ticker}`);
      setStockDetails(response.data);
    } catch (fetchError) {
      console.error('Error fetching stock details:', fetchError);
      setError(tt('Unable to load security details. Please try again.'));
    } finally {
      setDetailsLoading(false);
    }
  }, [apiBase, tt]);

  const fetchPriceHistory = useCallback(async (ticker, period) => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(`${apiBase}/stocks/${ticker}/history?period=${period}`);
      setPriceHistory(response.data.data || []);
    } catch (fetchError) {
      console.error('Error fetching price history:', fetchError);
      setError(tt('Unable to load price history. Please try again.'));
    } finally {
      setHistoryLoading(false);
    }
  }, [apiBase, tt]);

  useEffect(() => {
    fetchAllStocks();
    fetchFilterOptions();
  }, [fetchAllStocks, fetchFilterOptions]);

  useEffect(() => {
    const requestedTicker = String(searchParams.get('ticker') || '').trim().toUpperCase();
    if (!requestedTicker || requestedTicker === selectedStock) return;

    handleSelectStock(requestedTicker, {
      closeSuggestions: true,
      syncUrl: false,
    });
  }, [handleSelectStock, searchParams, selectedStock]);

  useEffect(() => {
    if (!searchTerm || searchTerm.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const response = await axios.get(`${apiBase}/stocks/search`, { params: { q: searchTerm, limit: 8 } });
        setSuggestions(response.data.results || []);
        setShowSuggestions(true);
      } catch { /* ignore */ }
    }, 200);
    return () => clearTimeout(timer);
  }, [searchTerm, apiBase]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!selectedStock) return;
    fetchStockDetails(selectedStock);
    fetchPriceHistory(selectedStock, selectedPeriod);
  }, [selectedStock, selectedPeriod, fetchStockDetails, fetchPriceHistory]);

  const filteredStocks = useMemo(() => (
    stocks.filter((stock) => {
      const matchesSearch = stock.ticker.toLowerCase().includes(searchTerm.toLowerCase())
        || stock.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSector = !selectedSector || stock.sector === selectedSector;
      const matchesExchange = !selectedExchange || stock.exchange === selectedExchange;
      return matchesSearch && matchesSector && matchesExchange;
    })
  ), [stocks, searchTerm, selectedSector, selectedExchange]);

  const formatPrice = useCallback((price) => (hasMetricValue(price) ? `$${parseFloat(price).toFixed(2)}` : tt('N/A')), [tt]);
  const formatPercent = useCallback((value) => (hasMetricValue(value) ? `${parseFloat(value).toFixed(2)}%` : tt('N/A')), [tt]);
  const formatRatio = useCallback((value) => (hasMetricValue(value) ? Number(value).toFixed(2) : tt('N/A')), [tt]);

  const formatLargeNumber = useCallback((num) => {
    if (!hasMetricValue(num)) return tt('N/A');
    const value = parseFloat(num);
    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    return `$${value.toLocaleString(locale)}`;
  }, [locale, tt]);

  const priceChange = useMemo(() => {
    if (priceHistory.length < 2) return { change: 0, percent: 0 };
    const first = priceHistory[0].close;
    const last = priceHistory[priceHistory.length - 1].close;
    const change = last - first;
    const percent = (change / first) * 100;
    return { change, percent };
  }, [priceHistory]);

  const priceDomain = useMemo(() => {
    const closes = priceHistory
      .map((point) => Number(point.close))
      .filter((value) => Number.isFinite(value));

    if (closes.length === 0) {
      return ['auto', 'auto'];
    }

    const min = Math.min(...closes);
    const max = Math.max(...closes);

    if (min === max) {
      const flatPadding = Math.max(min * 0.02, 1);
      return [Math.max(0, min - flatPadding), max + flatPadding];
    }

    const paddingByPeriod = {
      '1W': 0.16,
      '1M': 0.12,
      '3M': 0.1,
      '6M': 0.08,
      '1Y': 0.06,
      '5Y': 0.05,
      MAX: 0.04,
    };

    const range = max - min;
    const padding = range * (paddingByPeriod[selectedPeriod] ?? 0.08);
    const lower = Math.max(0, min - padding);
    const upper = max + padding;

    return [Number(lower.toFixed(2)), Number(upper.toFixed(2))];
  }, [priceHistory, selectedPeriod]);

  const formatAxisPrice = useCallback((value) => {
    if (!Number.isFinite(value)) return value;
    if (Math.abs(value) >= 1000) {
      return `$${value.toLocaleString(locale, { maximumFractionDigits: 0 })}`;
    }
    if (Math.abs(value) >= 100) {
      return `$${value.toFixed(0)}`;
    }
    if (Math.abs(value) >= 10) {
      return `$${value.toFixed(1)}`;
    }
    return `$${value.toFixed(2)}`;
  }, [locale]);

  const latestPriceDateLabel = useMemo(() => {
    if (!latestPriceDate) return null;
    const value = new Date(latestPriceDate);
    if (Number.isNaN(value.getTime())) return null;
    return value.toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' });
  }, [latestPriceDate, locale]);

  const kpiMetrics = useMemo(() => {
    if (!stockDetails) return [];

    return [
      {
        label: tt('Market Cap'),
        help: tt("Total market value of a company's outstanding shares. Calculated as share price × total shares outstanding."),
        value: formatLargeNumber(stockDetails.market_cap),
        available: hasMetricValue(stockDetails.market_cap),
      },
      {
        label: tt('P/E Ratio'),
        help: tt("Price-to-Earnings ratio. Compares a stock's price to its earnings per share. Lower P/E may indicate better value; higher P/E may reflect growth expectations."),
        value: formatRatio(stockDetails.pe_ratio),
        available: hasMetricValue(stockDetails.pe_ratio),
      },
      {
        label: tt('Beta'),
        help: tt("Measures a stock's volatility relative to the overall market. Beta of 1 = moves with market. Above 1 = more volatile. Below 1 = less volatile."),
        value: formatRatio(stockDetails.beta),
        available: hasMetricValue(stockDetails.beta),
      },
      {
        label: tt('Dividend Yield'),
        help: tt('Annual dividend payment as a percentage of the stock price. Shows how much income you earn per dollar invested, before any price changes.'),
        value: formatPercent(stockDetails.dividend_yield),
        available: hasMetricValue(stockDetails.dividend_yield),
      },
    ].filter((metric) => metric.available);
  }, [formatLargeNumber, formatPercent, formatRatio, stockDetails, tt]);

  const valuationMetrics = useMemo(() => {
    if (!stockDetails) return [];

    return [
      {
        label: tt('ROE'),
        help: tt('Return on Equity. Measures how effectively a company uses shareholder money to generate profit. Higher ROE generally indicates better management efficiency.'),
        value: formatPercent(stockDetails.roe),
        available: hasMetricValue(stockDetails.roe),
      },
      {
        label: tt('Debt/Equity'),
        help: tt('Ratio of total debt to shareholder equity. Shows how much a company relies on borrowed money. Lower is generally safer; high ratios may signal financial risk.'),
        value: formatRatio(stockDetails.debt_to_equity),
        available: hasMetricValue(stockDetails.debt_to_equity),
      },
      {
        label: tt('Expected Return'),
        help: tt("Annualized expected return estimated by the Fama-French 5-factor model. Reflects the stock's factor exposures, not a guarantee of future performance."),
        value: hasMetricValue(stockDetails.expected_return) ? `${(stockDetails.expected_return * 100).toFixed(2)}%` : tt('N/A'),
        available: hasMetricValue(stockDetails.expected_return),
        tone: stockDetails.expected_return >= 0 ? 'positive' : 'negative',
      },
      {
        label: tt('Volatility'),
        help: tt("Annualized standard deviation of the stock's daily returns. Higher values indicate larger price swings and greater uncertainty."),
        value: hasMetricValue(stockDetails.volatility) ? `${(stockDetails.volatility * 100).toFixed(2)}%` : tt('N/A'),
        available: hasMetricValue(stockDetails.volatility),
      },
    ].filter((metric) => metric.available);
  }, [formatPercent, formatRatio, stockDetails, tt]);

  const financialMetrics = useMemo(() => {
    if (!stockDetails) return [];

    return [
      {
        label: tt('Revenue'),
        help: tt('Total money a company earns from selling its products or services before any expenses are deducted.'),
        value: formatLargeNumber(stockDetails.revenue),
        available: hasMetricValue(stockDetails.revenue),
      },
      {
        label: tt('Net Income'),
        help: tt("A company's total profit after all expenses, taxes, and costs have been subtracted from revenue. Also called the 'bottom line'."),
        value: formatLargeNumber(stockDetails.net_income),
        available: hasMetricValue(stockDetails.net_income),
      },
      {
        label: tt('EPS Growth'),
        help: tt("Earnings Per Share growth. Shows how fast a company's profit per share is increasing. Higher growth often signals a company expanding its business."),
        value: formatPercent(stockDetails.eps_growth),
        available: hasMetricValue(stockDetails.eps_growth),
      },
      {
        label: tt('Operating Margin'),
        help: tt('Percentage of revenue remaining after paying operating costs (wages, materials, etc.). Higher margin = more efficient business.'),
        value: formatPercent(stockDetails.operating_margin),
        available: hasMetricValue(stockDetails.operating_margin),
      },
      {
        label: tt('Current Ratio'),
        help: tt("Company's ability to pay short-term debts. Calculated as current assets ÷ current liabilities. Above 1.0 means the company can cover its near-term obligations."),
        value: formatRatio(stockDetails.current_ratio),
        available: hasMetricValue(stockDetails.current_ratio),
      },
      {
        label: tt('Volume'),
        help: tt('Number of shares traded in a given period. Higher volume means more market interest and usually easier buying/selling (liquidity).'),
        value: hasMetricValue(stockDetails.volume) ? Number(stockDetails.volume).toLocaleString(locale) : tt('N/A'),
        available: hasMetricValue(stockDetails.volume),
      },
    ].filter((metric) => metric.available);
  }, [formatLargeNumber, formatPercent, formatRatio, locale, stockDetails, tt]);

  const renderMetricGrid = (metrics, sectionClass = '') => {
    if (!metrics.length) {
      return <div className="market-section-empty">{tt('No additional metrics available for this asset.')}</div>;
    }

    return (
      <div className={`market-metrics-grid ${sectionClass}`.trim()}>
        {metrics.map((metric) => (
          <div key={metric.label} className="market-metric-card">
            <p>{metric.label} <HelpIcon text={metric.help} /></p>
            <strong className={metric.tone || ''}>{metric.value}</strong>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="market-root">
      <div className="market-header">
        <div className="market-head-left">
          <div className="market-icon-wrap"><FiBarChart2 /></div>
          <div>
            <h2>{t('md_research_title', 'Research one asset at a time')}</h2>
            <p>{t('md_research_subtitle', 'Search first, then open fundamentals, price history, and advanced valuation details only when useful.')}</p>
            {latestPriceDateLabel && (
              <p>
                {t('md_price_data_as_of', 'Price data as of')} {latestPriceDateLabel}
                {tickersWithPrice > 0 && ` (${latestPriceTickerCount}/${tickersWithPrice} ${t('md_tickers_latest_date', 'tickers at latest date')})`}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="market-layout">
        <DisclosureCard
          title={t('md_browse_stocks_title', 'Browse stocks')}
          subtitle={t('md_browse_stocks_subtitle', 'Find the security you want to understand')}
          alwaysOpen
          className="market-left-disclosure"
        >
          <div className="market-search-wrap" ref={searchRef}>
            <FiSearch className="market-search-icon" />
            <input
              type="text"
              placeholder={tt('Search ticker or name...')}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className="market-suggestions">
                {suggestions.map((s) => (
                  <button
                    key={s.ticker}
                    type="button"
                    onClick={() => {
                      handleSelectStock(s.ticker, { closeSuggestions: true });
                      setSearchTerm(s.ticker);
                    }}
                    className="market-suggestion-item"
                  >
                    <div className="market-stock-item-main">
                      <div className="market-stock-item-topline">
                        <strong>{s.ticker}</strong>
                        {s.exchange && <em>{s.exchange}</em>}
                      </div>
                      <span>{s.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="market-filters">
            <select value={selectedSector} onChange={(event) => setSelectedSector(event.target.value)}>
              <option value="">{tt('All Sectors')}</option>
              {sectors.map((sector) => (
                <option key={sector} value={sector}>{sector}</option>
              ))}
            </select>
            <select value={selectedExchange} onChange={(event) => setSelectedExchange(event.target.value)}>
              <option value="">{tt('All Exchanges')}</option>
              {exchanges.map((exchange) => (
                <option key={exchange} value={exchange}>{exchange}</option>
              ))}
            </select>
          </div>

          <p className="market-stock-count">
            {interpolate(t('md_showing_count', 'Showing {{shown}} of {{total}} stocks'), {
              shown: filteredStocks.length,
              total: stocks.length,
            })}
          </p>

          <div className="market-stock-list-shell">
            <div className="market-stock-list">
              {filteredStocks.map((stock) => (
                <button
                  key={stock.ticker}
                  type="button"
                  className={`market-stock-item ${selectedStock === stock.ticker ? 'active' : ''}`}
                  onClick={() => handleSelectStock(stock.ticker, { closeSuggestions: true })}
                >
                  <div className="market-stock-item-main">
                    <div className="market-stock-item-topline">
                      <strong>{stock.ticker}</strong>
                      {stock.exchange && <em>{stock.exchange}</em>}
                    </div>
                    <span>{stock.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </DisclosureCard>

        <section className="market-main-panel">
          {!selectedStock && (
            <div className="market-glass market-empty-state">
              <div className="market-empty-copy">
                <p className="market-empty-overline">{tt('Asset Research')}</p>
                <h3>{t('md_search_asset_begin', 'Search for an asset to begin')}</h3>
                <p>{tt('Search by ticker or company name, browse the stock list on the left, then select one asset to begin research.')}</p>
                <p>
                  {interpolate(
                    t('md_choose_from_count', 'Choose from {{count}} stocks already available in your database.'),
                    { count: stocks.length },
                  )}
                </p>
              </div>
            </div>
          )}

          {selectedStock && detailsLoading && !stockDetails && (
            <article className="market-glass market-summary-card market-loading-card">
              <div className="market-summary-head market-summary-loading">
                <div className="market-summary-loading-left">
                  <LoadingSkeleton width="96px" height="28px" borderRadius="8px" />
                  <LoadingSkeleton width="220px" height="18px" borderRadius="8px" />
                  <LoadingSkeleton width="160px" height="14px" borderRadius="999px" />
                </div>
                <div className="market-summary-loading-right">
                  <LoadingSkeleton width="112px" height="32px" borderRadius="8px" />
                  <LoadingSkeleton width="136px" height="16px" borderRadius="8px" />
                </div>
              </div>
              <div className="market-metrics-grid market-kpi-grid">
                <LoadingSkeleton height="92px" borderRadius="14px" variant="rectangular" count={4} />
              </div>
            </article>
          )}

          {selectedStock && stockDetails && (
            <>
              <article className="market-glass market-summary-card">
                <div className="market-summary-head">
                  <div className="market-asset-heading">
                    <div className="market-asset-title-row">
                      <h3>{stockDetails.ticker}</h3>
                      <p>{stockDetails.name}</p>
                    </div>
                    <div className="market-meta-row">
                      {stockDetails.exchange && <span className="market-meta-badge">{stockDetails.exchange}</span>}
                      {stockDetails.sector && <span className="market-meta-badge">{stockDetails.sector}</span>}
                    </div>
                  </div>
                  <div className="market-price-area">
                    <strong>{formatPrice(stockDetails.current_price)}</strong>
                    <span className={priceChange.change >= 0 ? 'positive' : 'negative'}>
                      {priceChange.change >= 0 ? <FiTrendingUp /> : <FiTrendingDown />}
                      {formatPrice(Math.abs(priceChange.change))} ({priceChange.percent.toFixed(2)}%)
                    </span>
                  </div>
                </div>

                {renderMetricGrid(kpiMetrics, 'market-kpi-grid')}
              </article>

              <DisclosureCard
                title={tt('Price chart')}
                subtitle={tt('Track trend by time window')}
                alwaysOpen
                className="market-disclosure"
              >
                <div className="market-period-controls">
                  {periods.map((period) => (
                    <button
                      key={period}
                      type="button"
                      className={selectedPeriod === period ? 'active' : ''}
                      onClick={() => setSelectedPeriod(period)}
                    >
                      {period}
                    </button>
                  ))}
                </div>

                {historyLoading ? (
                  <div className="market-loading market-chart-loading">
                    <LoadingSkeleton width="100%" height="320px" borderRadius="14px" variant="rectangular" />
                  </div>
                ) : priceHistory.length > 0 ? (
                  <div className="market-chart-wrap">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={priceHistory} margin={{ top: 8, right: 14, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="marketAreaGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.2} />
                            <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(100, 116, 139, 0.2)" />
                        <XAxis
                          dataKey="date"
                          tick={{ fill: '#64748b', fontSize: 11 }}
                          tickFormatter={(date) => {
                            const d = new Date(date);
                            if (!d || isNaN(d)) return '';
                            if (selectedPeriod === '1W') {
                              return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
                            }
                            if (selectedPeriod === '1M') {
                              return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
                            }
                            if (selectedPeriod === '3M' || selectedPeriod === '6M') {
                              return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
                            }
                            // 1Y, 5Y, MAX: show "Jan '25" style
                            return d.toLocaleDateString(locale, { month: 'short', year: '2-digit' });
                          }}
                          axisLine={false}
                          tickLine={false}
                          minTickGap={selectedPeriod === '1W' || selectedPeriod === '1M' ? 30 : 50}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          tick={{ fill: '#64748b', fontSize: 11 }}
                          domain={priceDomain}
                          tickFormatter={formatAxisPrice}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            background: 'rgba(13, 19, 33, 0.95)',
                            border: '1px solid rgba(100, 116, 139, 0.28)',
                            borderRadius: 10,
                            color: '#e2e8f0',
                            fontSize: 12,
                          }}
                          formatter={(value) => [`$${parseFloat(value).toFixed(2)}`, tt('Price')]}
                          labelFormatter={(date) => new Date(date).toLocaleDateString(locale, {
                            month: 'long', day: 'numeric', year: 'numeric',
                          })}
                        />
                        <Area type="monotone" dataKey="close" stroke="#22d3ee" strokeWidth={2} fill="url(#marketAreaGrad)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="market-loading">{tt('No price data available.')}</div>
                )}
              </DisclosureCard>

              <DisclosureCard
                title={tt('Valuation & Risk')}
                subtitle={tt('Model context, risk measures, and capital structure')}
                alwaysOpen
                className="market-disclosure"
              >
                {renderMetricGrid(valuationMetrics)}
              </DisclosureCard>

              <DisclosureCard
                title={tt('Company Financials')}
                subtitle={tt('Revenue, earnings, operating strength, and liquidity')}
                alwaysOpen
                className="market-disclosure"
              >
                {renderMetricGrid(financialMetrics, 'financials')}
              </DisclosureCard>
            </>
          )}

          {error && <div className="market-error">{error}</div>}
        </section>
      </div>
    </div>
  );
};

export default MarketData;
